import React, { useMemo } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { Network } from '../../../services/token.service'
import { useStoreState } from '../../../model/hooks'
import { calculateRatioFromTickForTokens, numFormat } from '../../../blockchainUtils/math'
import BigNumber from 'bignumber.js'
import { MAX_TICK } from '../helper'

interface RangeSliderChartProps {
    data: { tick: number; liq: number }[]
    isReversed: boolean | undefined
    network: Network
    chartHeight?: number
    isMiminal?: boolean
    currentTickInRange?: number
    token0Symbol?: string
    token1Symbol?: string
    token1Address?: string
    token2Address?: string
}

function RangeSliderChart(props: RangeSliderChartProps) {
    const { data, isReversed, network, currentTickInRange, isMiminal, chartHeight = 150, token0Symbol = '', token1Symbol = '', token1Address = '', token2Address = '' } = props
    const renderRangeChart = useMemo(() => {
        if (network !== 'mainnet-alpha')
            return (
                <div style={{ height: `${chartHeight}px` }} className={`hidden lg:flex min-h-[${chartHeight}px] h-[${chartHeight}px] w-full flex`}>
                    <span className='text-[white] text-[10px] opacity-[0.4]'>No liquidity chart for testnets</span>
                </div>
            )
        if (!data || data.length === 0)
            return (
                <div style={{ height: `${chartHeight}px` }} className={`hidden lg:flex opacity-[0.4] min-h-[${chartHeight}px] h-[${chartHeight}px] w-full flex`}>
                    <span className='text-[10px]'>loading liquidity distribution chart...</span>
                </div>
            )

        function renderXAxis() {
            return (
                <XAxis
                    reversed={isReversed}
                    domain={[`dataMin`, 'dataMax']}
                    dataKey='tick'
                    axisLine={{ stroke: '#0AE8F9', fill: '#0AE8F9' }}
                    type='number'
                    // axisLine={false}
                    tick={false}
                    tickLine={{
                        stroke: '#0AE8F9',
                        strokeOpacity: 0.2,
                        strokeWidth: 1
                    }}
                    tickCount={5}
                    interval={'preserveStartEnd'}
                />
            )
        }
        return (
            <ResponsiveContainer className={`${isMiminal ? 'hidden lg:flex' : ''}`} width={'100%'} height={chartHeight}>
                <BarChart height={chartHeight} margin={{ top: 0, left: 0, right: 0, bottom: 0 }} barGap={0} data={data} reverseStackOrder={isReversed}>
                    {renderXAxis()}
                    <YAxis height={chartHeight} hide={true} padding={{ bottom: 0 }} />
                    {isMiminal ? null : <CartesianGrid vertical={false} stroke='#549EFC' strokeWidth={1} strokeDasharray='5' opacity={0.2} />}

                    <Bar animationDuration={0} height={chartHeight} dataKey='liq' opacity={1}>
                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                stroke={`${currentTickInRange && currentTickInRange !== 0 && currentTickInRange === index ? '#FF5353' : ' #0AE8F9'}`}
                                fill={`${currentTickInRange !== 0 && currentTickInRange === index ? '#FF5353' : ' #0AE8F9'}`}
                            />
                        ))}
                    </Bar>
                    {isMiminal ? null : (
                        <Tooltip
                            // @ts-ignore
                            content={<TooltipInfo token1Address={token1Address} token2Address={token2Address} token0Symbol={token0Symbol} token1Symbol={token1Symbol} />}
                        />
                    )}
                </BarChart>
            </ResponsiveContainer>
        )
    }, [isReversed, data])

    return renderRangeChart
}

export default RangeSliderChart

export const TooltipInfo = ({
    active,
    payload,
    label,
    token0Symbol,
    token1Symbol,
    token1Address,
    token2Address
}: {
    active?: any
    payload?: any
    label?: any
    token0Symbol: string
    token1Symbol: string
    token1Address: string
    token2Address: string
}) => {
    const value = payload?.[0]?.payload?.tick
    const getTokenByAddress = useStoreState((state) => state.tokens.getToken)
    const timestamp = Number(label) * 1000

    let token1 = getTokenByAddress('mainnet-alpha', token1Address)
    let token2 = getTokenByAddress('mainnet-alpha', token2Address)

    const isTokensReversed = BigNumber(token1?.address!).gt(token2?.address!)

    // note: it seems tokens are never reversed here. so the entire logic of is reversed is not needed
    /*  if (isTokensReversed) {
        const prevToken1 = token1
        token1 = token2
        token2 = prevToken1
    }*/

    const ratio = token1 && token2 ? calculateRatioFromTickForTokens(value - MAX_TICK, token1, token2) : 0
    if (!active || !value || !label || !timestamp || !ratio) return <></>

    const token1Price = numFormat(ratio.toString())
    const token2Price = numFormat((1 / ratio.toNumber()).toString())
    return (
        <div className='bg-[#242C58] text-[white] rounded-[8px] py-3 px-4 shadow-2xl opacity-[0.8]'>
            <div className='flex items-center'>
                <span>{`1 ${token1Symbol} = ${isTokensReversed ? token2Price : token1Price} ${token0Symbol}`}</span>
            </div>
            <div className='flex items-center'>
                <span>{`1 ${token0Symbol} = ${isTokensReversed ? token1Price : token2Price} ${token1Symbol}`}</span>
            </div>
        </div>
    )
}
